import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { NewCampaignForm } from "../common/send-action/NewCampaignForm";
import TemplatePickerDialog from "../common/send-action/TemplatePickerDialog";
import { TemplateContentPreview } from "../common/send-action/TemplateContentPreview";
import { UploadRecipientSet } from "../common/send-action/UploadRecipientSet";
import CampaignSelectionDialog from "../common/send-action/CampaignSelectionDialog";
import SelectCampaignDialog from "../common/send-action/SelectCampaignDialog";
import type { IZnsTemplate } from "modules/outbound-message/types/znsTemplate";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  submitAutoZnsCampaign,
  resetCreateStatus,
  selectZnsCampaignCreateLoading,
  selectZnsCampaignCreateSuccess,
  selectZnsCampaignCreateError,
  loadZnsCampaignConditions,
  selectZnsCampaignConditions,
} from "../store/znsCampaignSlice";
import {
  filterCampaignConditions,
  validCampaignConditions,
} from "../utils/campaignSendConditions.util";
import ZnsCampaignService from "../services/znsCampaignService";
import { toastEmitter } from "utils/toast.util";
import ReviewCampaignModal from "../common/send-action/ReviewCampaignModal";
import { SendConditions } from "../common/send-action/SendConditions";
import SendIcon from "@mui/icons-material/Send";
import { Moment } from "moment";

/**
 * Auto sending page for Customer: create campaign, choose template via picker dialog, set recipients.
 */
const AutoSendingFormPage = ({
  mode = "customer",
}: {
  mode?: "customer" | "outlet-owner";
}) => {
  const dispatch = useAppDispatch();
  const isSubmitting = useAppSelector(selectZnsCampaignCreateLoading);
  const createSuccess = useAppSelector(selectZnsCampaignCreateSuccess);
  const createError = useAppSelector(selectZnsCampaignCreateError);

  const conditionList = useAppSelector(selectZnsCampaignConditions);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IZnsTemplate | null>(
    null,
  );
  const [campaignName, setCampaignName] = useState("");
  const [recipientFilePath, setRecipientFilePath] = useState<string | null>(
    null,
  );
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [selectCampaignDialogOpen, setSelectCampaignDialogOpen] =
    useState(false);
  const [activeCampaignIndex, setActiveCampaignIndex] = useState<number | null>(
    null,
  );
  const [campaignType, setCampaignType] = useState<"OL" | "ZNS">("ZNS");
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [sendConditions, setSendConditions] = useState<{
    cron: string;
    endDate: Moment | null;
    campaignConfigs: any[];
  } | null>(null);
  const resetUploadItemsRef = useRef<(() => void) | null>(null);

  const handleSelectFromCampaign = () => {
    setSelectionDialogOpen(true);
  };

  const handleTypeSelect = (type: "OL" | "ZNS") => {
    setCampaignType(type);
    setSelectionDialogOpen(false);
    setSelectCampaignDialogOpen(true);
  };

  const handleCampaignSelect = (campaign: any) => {
    if (activeCampaignIndex !== null) {
      // Logic for SendConditions
      setSendConditions((prev) => {
        if (!prev) return prev;
        const newConfigs = [...prev.campaignConfigs];
        newConfigs[activeCampaignIndex] = {
          ...newConfigs[activeCampaignIndex],
          campaignId: campaign.id,
          campaignName: campaign.name,
        };
        return { ...prev, campaignConfigs: newConfigs };
      });
      setActiveCampaignIndex(null);
    } else {
      // Existing logic for UploadRecipientSet
      setSelectedCampaign(campaign);
    }
    setSelectCampaignDialogOpen(false);
  };

  useEffect(() => {
    dispatch(loadZnsCampaignConditions());
  }, [dispatch]);

  useEffect(() => {
    if (createSuccess) {
      toastEmitter.show(
        "Campaign created and notifications generated successfully!",
        "success",
      );
      setCampaignName("");
      setSelectedTemplate(null);
      setRecipientFilePath(null);
      dispatch(resetCreateStatus());
    }
    if (createError) {
      toastEmitter.show(
        createError.Message || "Failed to create campaign.",
        "error",
      );
      dispatch(resetCreateStatus());
    }
  }, [createSuccess, createError, dispatch]);

  const hanldeUploadFile = useCallback(
    async (file: File) => {
      if (!selectedTemplate?.id) {
        toastEmitter.show(
          "Please select a template before uploading recipients.",
          "warning",
        );
        return;
      }
      const result = await ZnsCampaignService.uploadRecipients(
        selectedTemplate.id,
        file,
      );
      setRecipientFilePath(result.filePath);
    },
    [selectedTemplate],
  );

  const handleSelectTemplate = useCallback((template: IZnsTemplate) => {
    setSelectedTemplate(template);
  }, []);

  const handleOpenReview = useCallback(() => {
    if (!selectedTemplate || !campaignName) {
      toastEmitter.show(
        "Please enter campaign name and select a template",
        "warning",
      );
      return;
    }

    const hasCampaignConfig = sendConditions?.campaignConfigs?.some(
      (c) =>
        c.campaignId &&
        c.conditions.some((cond: any) => cond.fieldName && cond.value),
    );

    if (!sendConditions || (!sendConditions.cron && !hasCampaignConfig)) {
      toastEmitter.show(
        "Please set up at least one condition (Cron or OL/ZNS Campaign)",
        "warning",
      );
      return;
    }

    setReviewOpen(true);
  }, [campaignName, selectedTemplate, sendConditions]);

  const handleConfirmSend = useCallback(() => {
    const hasCampaignConfig = sendConditions?.campaignConfigs?.some(
      (c) =>
        c.campaignId &&
        c.conditions.some((cond: any) => validCampaignConditions(cond)),
    );

    if (!sendConditions || (!sendConditions.cron && !hasCampaignConfig)) {
      toastEmitter.show(
        "Please set up at least one condition (Cron or OL/ZNS Campaign)",
        "warning",
      );
      return;
    }

    dispatch(
      submitAutoZnsCampaign({
        campaignName,
        znsTemplateId: selectedTemplate?.id || "",
        recipientFilePath,
        mode,
        sendConditions: {
          cron: sendConditions.cron,
          endDateIso: sendConditions.endDate?.toISOString() ?? null,
          campaignConfigs: sendConditions.campaignConfigs,
        },
      }),
    );
    setReviewOpen(false);
  }, [
    campaignName,
    selectedTemplate,
    recipientFilePath,
    dispatch,
    sendConditions,
    mode,
  ]);

  const filteredCampaignConfigs = useMemo(() => {
    return filterCampaignConditions(sendConditions?.campaignConfigs || []);
  }, [sendConditions]);

  return (
    <Container
      maxWidth={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          gap: 3,
        }}
      >
        <Box
          sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: 15 }}
            onClick={handleOpenReview}
            disabled={isSubmitting}
          >
            <SendIcon sx={{ fontSize: 18 }} /> &nbsp;
            {isSubmitting ? "Sending..." : "Send ZNS"}
          </Button>
        </Box>
        <Box sx={{ width: "50%" }}>
          <NewCampaignForm
            selectedTemplate={selectedTemplate}
            onSelectTemplateClick={() => setTemplateDialogOpen(true)}
            campaignName={campaignName}
            onCampaignNameChange={setCampaignName}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          gap: 1.5,
          flexDirection: "column",
          display: "flex",
        }}
      >
        <TemplateContentPreview
          parameters={selectedTemplate?.parameters || []}
          templateId={selectedTemplate?.id}
        />
        <UploadRecipientSet
          uploadFile={hanldeUploadFile}
          onReset={(resetFn: () => void) =>
            (resetUploadItemsRef.current = resetFn)
          }
          mode="simple"
          onSelectFromCampaign={handleSelectFromCampaign}
          disabled={!selectedTemplate}
          selectedCampaign={selectedCampaign}
        />
        <SendConditions
          conditionConfigList={conditionList}
          disabled={!selectedTemplate}
          value={sendConditions}
          onChange={setSendConditions}
          onOpenSelectCampaign={(index) => {
            setActiveCampaignIndex(index);
            setCampaignType("OL");
            setSelectCampaignDialogOpen(true);
          }}
        />
      </Box>
      <TemplatePickerDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelectTemplate={handleSelectTemplate}
        selectedTemplate={selectedTemplate}
      />
      <CampaignSelectionDialog
        open={selectionDialogOpen}
        onClose={() => setSelectionDialogOpen(false)}
        onSelect={handleTypeSelect}
      />
      <SelectCampaignDialog
        open={selectCampaignDialogOpen}
        onClose={() => setSelectCampaignDialogOpen(false)}
        onSelect={handleCampaignSelect}
        type={campaignType}
      />
      <ReviewCampaignModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleConfirmSend}
        title="Review Automation Set-up"
        subtitle="Please check the information before saving the automatic message!"
        campaignName={campaignName}
        templateName={selectedTemplate?.templateName || ""}
        details={[
          ...(selectedCampaign
            ? [{ label: "Campaign", value: selectedCampaign?.name || "" }]
            : []),
          ...(sendConditions?.cron && sendConditions?.cron !== ""
            ? [
                {
                  label: "Time Config (Cron)",
                  value: sendConditions?.cron || "",
                },
              ]
            : []),
          ...(sendConditions?.endDate
            ? [
                {
                  label: "End Date",
                  value: sendConditions?.endDate?.format("DD/MM/YYYY"),
                },
              ]
            : []),
          ...((filteredCampaignConfigs ?? []).length > 0
            ? [
                {
                  label: "Send Conditions",
                  value: filteredCampaignConfigs
                    ?.map(
                      (config) =>
                        `${config.logicalOperator ? config.logicalOperator + "\n" : ""} ${config.campaignName}\n${config.conditions.map((cond: any) => `${cond.fieldName} ${cond.comparisonOperator} ${cond.value}`).join("\n")}`,
                    )
                    .join("\n"),
                },
              ]
            : []),
        ]}
        isSubmitting={isSubmitting}
      />
    </Container>
  );
};

export default React.memo(AutoSendingFormPage);
